import * as React from "react";

import { RobotoTypography } from "@/shared/components/RobotoTypography";
import {
  type ChatToolResultContent,
  type ChatToolUseContent,
} from "@/shared/domain/chat";
import { useToolDetails } from "@/shared/domain/chat/hooks";

import styles from "./ToolDetailPanel.module.css";

function stringifyJson(value: unknown): string {
  return JSON.stringify(value ?? null, null, 2) ?? "null";
}

interface JsonSectionProps {
  label: string;
  value: unknown;
}

function JsonSection({ label, value }: JsonSectionProps): React.JSX.Element {
  return (
    <div className={styles.section}>
      <RobotoTypography component="p" className={styles.sectionTitle}>
        {label}
      </RobotoTypography>
      <pre className={styles.jsonBlock}>{stringifyJson(value)}</pre>
    </div>
  );
}

interface ToolDetailPanelContentProps {
  toolResult: ChatToolResultContent;
  toolUse: ChatToolUseContent;
}

function ToolDetailPanelContent({
  toolResult,
  toolUse,
}: ToolDetailPanelContentProps): React.JSX.Element {
  return (
    <div className={styles.panel}>
      <RobotoTypography component="p" className={styles.title}>
        Tool details for <b>{toolResult.tool_name}</b>
      </RobotoTypography>
      <JsonSection label="Request" value={toolUse.raw_request} />
      <JsonSection label="Response" value={toolResult.raw_response} />
    </div>
  );
}

interface ToolDetailPanelProps {
  chatId: string;
  toolUseId: string;
}

export function ToolDetailPanel({
  chatId,
  toolUseId,
}: ToolDetailPanelProps): React.JSX.Element | null {
  const { data, error, isFetching, isPending, refetch } = useToolDetails(
    chatId,
    toolUseId,
  );

  React.useEffect(() => {
    void refetch();
  }, [chatId, refetch, toolUseId]);

  if ((isPending || isFetching) && data === undefined) {
    return (
      <div className={styles.panel}>
        <RobotoTypography component="p" className={styles.loadingText}>
          Loading tool details…
        </RobotoTypography>
      </div>
    );
  }

  if (error && data === undefined) {
    return (
      <div className={styles.panel}>
        <RobotoTypography component="p" className={styles.title}>
          Failed to load tool details
        </RobotoTypography>
        <RobotoTypography component="p" className={styles.errorText}>
          {error.message}
        </RobotoTypography>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <ToolDetailPanelContent
      toolResult={data.tool_result}
      toolUse={data.tool_use}
    />
  );
}
